import { AlertCircle, Check, Cloud, CloudOff } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export type SaveStatus = "saving" | "saved" | "offline" | "error" | "auth";

interface SaveStatusIndicatorProps {
  status: SaveStatus;
  authenticated: boolean;
  email: string | null;
  sheetOpenUrl?: string | null;
  onSignOut?: () => void;
  onSignIn?: () => void;
  onRetry?: () => void;
}

const statusConfig = {
  saving: {
    icon: Cloud,
    text: "Saving...",
    color: "text-[var(--color-text-tertiary)]",
  },
  saved: {
    icon: Check,
    text: "Saved",
    color: "text-[var(--color-success-text)]",
  },
  offline: {
    icon: CloudOff,
    text: "Offline - will sync",
    color: "text-[var(--color-warning-text)]",
  },
  error: {
    icon: AlertCircle,
    text: "Sync error",
    color: "text-[var(--color-error-text)]",
  },
  auth: {
    icon: AlertCircle,
    text: "Sign in to sync",
    color: "text-[var(--color-warning-text)]",
  },
} satisfies Record<SaveStatus, { icon: typeof Check; text: string; color: string }>;

export function SaveStatusIndicator({
  status,
  authenticated,
  email,
  sheetOpenUrl,
  onSignOut,
  onSignIn,
  onRetry,
}: SaveStatusIndicatorProps) {
  const config = statusConfig[status];
  const displayText = authenticated ? config.text : "Sign in to sync";
  const displayColor = authenticated ? config.color : "text-[var(--color-warning-text)]";
  const Icon = authenticated ? config.icon : AlertCircle;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!menuRef.current) {
        return;
      }
      if (!menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    window.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-[var(--color-border-subtle)] bg-[var(--color-bg-base)]/95 py-3 backdrop-blur-sm">
      <div className="mx-auto flex max-w-[1100px] items-center justify-between gap-3 px-4">
        <div className="relative" ref={menuRef}>
          {!authenticated ? (
            <button
              type="button"
              onClick={onSignIn}
              className="rounded-md border border-[var(--color-border-medium)] px-2 py-1 text-xs text-[var(--color-text-primary)]"
            >
              Continue with Google
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setMenuOpen(previous => !previous)}
                className="rounded-md border border-[var(--color-border-medium)] px-2 py-1 text-xs text-[var(--color-text-primary)]"
              >
                {email ?? "Signed in"}
              </button>
              {menuOpen && (
                <div className="absolute bottom-full left-0 mb-2 min-w-[220px] rounded-lg border border-[var(--color-border-medium)] bg-[var(--color-bg-base)] p-2 shadow-lg">
                  {sheetOpenUrl && (
                    <a
                      href={sheetOpenUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => setMenuOpen(false)}
                      className="mb-2 flex h-12 items-center rounded-lg border-2 border-transparent px-3 text-base text-[var(--color-text-primary)] transition-all duration-[var(--transition-base)] hover:bg-[var(--color-bg-hover)]"
                    >
                      View sheet
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      onSignOut?.();
                    }}
                    className="flex h-12 w-full items-center rounded-lg border-2 border-transparent px-3 text-left text-base text-[var(--color-text-primary)] transition-all duration-[var(--transition-base)] hover:bg-[var(--color-bg-hover)]"
                  >
                    Sign out
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className={`flex items-center gap-2 text-sm ${displayColor}`}>
          <Icon size={16} strokeWidth={2} />
          <span>{displayText}</span>
          {status === "error" && onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="rounded-md border border-[var(--color-border-medium)] px-2 py-0.5 text-xs text-[var(--color-text-primary)] transition-colors duration-[var(--transition-base)] hover:bg-[var(--color-bg-hover)]"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
